/**
 * What the property form may offer, and what pressing it will do.
 *
 * Task 0096's widening half, and @Codex's ruling on it. The form has always
 * hidden roles the open object already carries — correctly, since a second key
 * for one role makes every later write to it ambiguous. But enum options are
 * DERIVED from the values a pool holds, so a pool with one value has a chooser
 * with one option, and there was no gesture anywhere that could give it a
 * second. The bootstrap trap @Codex named for relations, in a different
 * mechanism: *"the pool has no gesture capable of creating the reference that
 * would raise its density."*
 *
 * The exception is deliberately narrow. A carried **enum** comes back, because
 * this is the only route to inventing a value the pool does not yet hold.
 * Carried text, number, score, date, media and reference roles stay out: each
 * of those is edited in its own field on the card, so re-offering them would
 * turn this form back into a duplicate-key door for no gain.
 *
 * It lives outside `main.ts` because a policy reachable only by opening a
 * `<select>` in a browser is a policy no unit test can state — the same reason
 * `template-seed.ts` was lifted out one task earlier.
 *
 * @Codex's, on task 0096.
 */
import { ROLE_KIND, type Role } from '@morph/spec';

const ADDABLE = new Set(['text', 'number', 'score', 'date', 'enum']);

/** Roles the property form may offer for the object in hand.
 *
 * A carried enum is intentionally exceptional: choosing it is the explicit
 * route for widening the pool's derived enum domain. All other carried roles
 * remain edits through their ordinary field, not additions through this form.
 */
export function propertyPickerRoles(
  roles: readonly Role[], carried: ReadonlySet<string>, keyIsReachable: (role: Role) => boolean,
): Role[] {
  return roles.filter((role) => ADDABLE.has(ROLE_KIND[role])
    && (!carried.has(role) || ROLE_KIND[role] === 'enum')
    && keyIsReachable(role));
}

/** Which act the form is about to perform, in the word a person reads.
 *
 *  Selecting a carried enum overwrites the slot it already has. Presenting that
 *  as "Add" would tell somebody they were creating a second property, which is
 *  both wrong and the exact confusion the exclusion rule existed to prevent. */
export function propertyPickerAct(
  role: Role | undefined, carried: ReadonlySet<string>,
): 'Add' | 'Change' {
  return role !== undefined && ROLE_KIND[role] === 'enum' && carried.has(role) ? 'Change' : 'Add';
}
